import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { Table, TableLazyLoadEvent, TableRowSelectEvent } from 'primeng/table';
import { AssetManagementControllerGetAll$Params } from 'src/app/api/fn/asset-management/asset-management-controller-get-all';
import { buildSerialSearch } from './serial-search';
import { AssetManagementItem, AssetManagementListResponse, BaseResponse } from 'src/app/api/models';
import { AssetManagementService, IdentityService } from 'src/app/api/services';
import { AuthService } from 'src/app/services/auth/auth.service';
import { enumAssetManagementPurpose } from '../enums/asset-management.enum';
import {
  catchError,
  concat,
  concatMap,
  debounceTime,
  filter,
  finalize,
  map,
  merge,
  mergeAll,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-modal-serial-in-stock',
  templateUrl: './modal-serial-in-stock.component.html',
  styleUrls: ['./modal-serial-in-stock.component.scss'],
})
export class ModalSerialInStockComponent {
  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  assets: AssetManagementItem[] = [];
  totalRecords: number = 0;
  question_title: string;
  warehouse_id?: number;
  product_id?: number;
  related_agreeement_id?: number;
  related_delivery_id: number | null = null;
  loading = false;
  purpose?: enumAssetManagementPurpose;
  selection = false;
  loadedAllAssets = false;
  preselectedAssets: string[] = [];
  selectedAssets: AssetManagementItem[] = [];
  // ricerca in corso: scrivendo si fanno più chiamate e conta solo l'ultima
  private loadSub?: Subscription;
  // selectedAssetsControl = new FormControl<string[]>([], {nonNullable: true});

  @ViewChild('dt1', { static: true }) table?: Table;

  frm_search = new FormGroup({
    search: new FormControl<string>(''),
    skip: new FormControl<number>(0),
    pageSize: new FormControl<number>(this.rowsPerPageOptions[0]),
  });

  constructor(
    public auth: AuthService,
    private assetMgmtService: AssetManagementService,
    private userService: IdentityService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService
  ) {
    this.purpose = config.data.purpose;
    this.preselectedAssets = config.data!.selectedAssets || [];
    // this.selectedAssetsControl.patchValue(this.selectedAssets);
    this.selection = config.data!.selection || false;
    this.question_title = config.data!.question_title;
    this.warehouse_id = config.data!.warehouse_id;
    this.product_id = config.data!.product_id;
    this.related_agreeement_id = config.data?.related_agreeement_id;
    this.related_delivery_id = config.data!.related_delivery_id || null;
    if (config.data!.related_delivery_id !== null) {
      this.related_delivery_id = Number(config.data!.related_delivery_id);
    }
  }

  ngOnInit() {
    this.frm_search
      .get('search')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => {
        const { serial_numbers } = buildSerialSearch(this.frm_search.value.search);
        this.frm_search.get('skip')?.setValue(0);
        // con un elenco incollato si mostrano subito tutti i seriali richiesti
        this.frm_search
          .get('pageSize')
          ?.setValue(serial_numbers?.length ?? this.rowsPerPageOptions[0]);
        this.loadAssets();
      });
    if (this.preselectedAssets.length === 0) {
      this.loadedAllAssets = true;
    } else {
      merge(
        this.assetMgmtService
          .assetManagementControllerGetAll({
            limit: this.preselectedAssets.length,
            skip: 0,
            serial_numbers: this.preselectedAssets,
          })
          .pipe(
            catchError(() =>
              of({
                result: false,
                message: '',
                data: {
                  list: [],
                  count: 0,
                },
              } as BaseResponse & {
                'data'?: AssetManagementListResponse;
              })
            )
          )
      )
        .pipe(finalize(() => (this.loadedAllAssets = true)))
        .subscribe({
          next: res => {
            this.selectedAssets.push(...res.data.list);
          },
          error: err => {
            //Ignore error
          },
        });
    }
    this.loadAssets();
  }

  loadAssets() {
    this.loading = true;
    const search = buildSerialSearch(this.frm_search.value.search);
    const params: AssetManagementControllerGetAll$Params = {
      limit: this.frm_search.get('pageSize')?.value!,
      skip: this.frm_search.get('skip')?.value!,
      related_warehouse_id: this.warehouse_id,
      related_product_id: this.product_id,
      serial_number: search.serial_number,
      serial_numbers: search.serial_numbers,
      purpose: this.purpose,
      ...(this.related_agreeement_id && {
        related_agreement_id: this.related_agreeement_id,
      }),
    };
    // if (event.globalFilter != null) {
    //   if (event.globalFilter[0] != '') {
    //     params.serial_number = event.globalFilter[0];
    //   }
    // }
    this.loadSub?.unsubscribe();
    this.loadSub = this.assetMgmtService.assetManagementControllerGetAll(params).subscribe({
      next: res => {
        this.assets = res.data.list;
        this.totalRecords = res.data.count;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
  close() {
    this.ref.close();
  }
  save() {
    this.ref.close({ selectedAssets: this.selectedAssets });
  }

  isRowSelectable = (event: TableRowSelectEvent) => {
    // un asset già partito con un DDT di consegna non è movimentabile (quello di un trasferimento sì)
    const assetFromDeliveryDdt = !!event.data.related_ddt_id && !event.data.related_ddt_is_transfer;
    return (
      !assetFromDeliveryDdt &&
      (!event.data.related_delivery_id || event.data.related_delivery_id === this.related_delivery_id)
    );
  };

  onPageChange(event: PaginatorState) {
    this.frm_search.get('skip')?.setValue(event.first || 0);
    this.frm_search.get('pageSize')?.setValue(event.rows || this.rowsPerPageOptions[0]);
    this.loadAssets();
  }
}
